import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button, AutoComplete, Input, DatePicker } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { setFilters } from "../expenseRecordSlice";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange"
import { fetchExpenseRecord, searchExpenses, fetchEmployees, fetchLocations, fetchUsers } from "../expenseRecordThunk";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const ExpenseRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.expenseRecord);
  const { searchResults, employees, locations, users, loadingSearch } = useSelector((state) => state.expenseRecord);
  
  useDefaultDateRange(setFilters, filters, "today");

  const [filterTypes] = useState([
    { filterType: "All" },
    { filterType: "By Expense" },
    { filterType: "By Employee" },
    { filterType: "By User" },
  ]);

  const [recordTypes] = useState([
    { recordType: "without details" },
    { recordType: "with details" },
  ]);

  const [expenseName, setExpenseName] = useState("");


  useEffect(() => {
    if (expenseName.trim() !== "") {
      dispatch(searchExpenses(expenseName));
    }
  }, [expenseName, dispatch]);

  useEffect(() => {
    if (filters.selectedFilterType?.filterType === "By User" && users.length === 0) {
      dispatch(fetchUsers());
    }

    if ((filters.selectedFilterType?.filterType === "By Location" ||
      filters.selectedFilterType?.filterType === "By Item") &&
      locations.length === 0) {
      dispatch(fetchLocations());
    }

    if (filters.selectedFilterType?.filterType === "By Employee" && employees.length === 0) {
      dispatch(fetchEmployees());
    }
  }, [filters.selectedFilterType, dispatch, users.length, locations.length, employees.length]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.selectedFilterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    if (filters.selectedFilterType.filterType === "By Expense" && !filters.selectedExpense) {
      messageApi.warning("Select Expense");
      return;
    }

    if (filters.selectedFilterType.filterType === "By Employee" && !filters.selectedEmployee) {
      messageApi.warning("Select Employee");
      return;
    }

    if (filters.selectedFilterType.filterType === "By User" && !filters.selectedUser) {
      messageApi.warning("Select User");
      return;
    }

    if (!filters.fromDate || !filters.toDate) {
      messageApi.warning("Select Date Range");
      return;
    }

    const payload = {
      selectedFilterType: filters.selectedFilterType,
      selectedExpense: filters.selectedExpense,
      selectedEmployee: filters.selectedEmployee,
      selectedUser: filters.selectedUser,
      selectedLocation: filters.selectedLocation,
      selectedRecordType: filters.selectedRecordType,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };

    dispatch(fetchExpenseRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch report ❌");
      }
    });
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={16} style={{ alignItems: "center" }}>
        <Col span={4}>
          <label>Filter Type</label>
          <AutoComplete
            style={{ width: "100%" }}
            options={filterTypes.map((item) => ({
              label: item.filterType,
              value: item.filterType,
              obj: item,
            }))}
            value={filters.selectedFilterType?.filterType}
            onSelect={(value, option) => {
              handleFilterChange("selectedFilterType", option.obj);
            }}
          >
            <Input placeholder="Filter Type" />
          </AutoComplete>
        </Col>

        {filters.selectedFilterType?.filterType === "By Expense" && (
          <Col span={4}>
            <label>Search Expense</label>
            <SearchListAutoComplete
              entityType="expense"
              searchThunk={(text) => setExpenseName(text)}
              resultSelector={(state) => state.expenseRecord.searchResults}
              loading={loadingSearch}
              onSelect={(obj) => {
                handleFilterChange("selectedExpense", obj);
              }}
              placeholder="Search Expense"
              showAddNew={false}
            />
          </Col>
        )}

        {filters.selectedFilterType?.filterType === "By Employee" && (
          <Col span={4}>
            <label>Select Employee</label>
            <AutoComplete
              style={{ width: "100%" }}
              options={employees.map((item) => ({
                label: item.display_text,
                value: item.display_text,
                obj: item,
              }))}
              value={filters.selectedEmployee?.display_text}
              onSelect={(value, option) => {
                handleFilterChange("selectedEmployee", option.obj);
              }}
            >
              <Input placeholder="Select Employee" />
            </AutoComplete>
          </Col>
        )}

        {(filters.selectedFilterType?.filterType === "By Location" ||
          filters.selectedFilterType?.filterType === "By Item") && (
            <Col span={4}>
              <label>Select Location</label>
              <AutoComplete
                style={{ width: "100%" }}
                options={locations.map((item) => ({
                  label: item.location_name,
                  value: item.location_name,
                  obj: item,
                }))}
                value={filters.selectedLocation?.location_name}
                onSelect={(value, option) => {
                  handleFilterChange("selectedLocation", option.obj);
                }}
              >
                <Input placeholder="Select Location" />
              </AutoComplete>
            </Col>
          )}

        {filters.selectedFilterType?.filterType === "By User" && (
          <Col span={4}>
            <label>Select User</label>
            <AutoComplete
              style={{ width: "100%" }}
              options={users.map((item) => ({
                label: item.name,
                value: item.name,
                obj: item,
              }))}
              value={filters.selectedUser?.name}
              onSelect={(value, option) => {
                handleFilterChange("selectedUser", option.obj);
              }}
            >
              <Input placeholder="Select User" />
            </AutoComplete>
          </Col>
        )}

        {filters.selectedFilterType && !(filters.selectedFilterType.filterType === "By Expense") && (
          <Col span={4}>
            <label>Record Type</label>
            <AutoComplete
              style={{ width: "100%" }}
              options={recordTypes.map((item) => ({
                label: item.recordType,
                value: item.recordType,
                obj: item,
              }))}
              value={filters.selectedRecordType?.recordType}
              onSelect={(value, option) => {
                handleFilterChange("selectedRecordType", option.obj);
              }}
            >
              <Input placeholder="Record Type" />
            </AutoComplete>
          </Col>
        )}

        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
          handleFilterChange("fromDate", fromDate);
          handleFilterChange("toDate", toDate);
          }}
        />

        <Col span={4} style={{ display: "flex", alignItems: "flex-end", height: 58 }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
            style={{height: 35.19}}
            data-testid="report-btn"
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default ExpenseRecordFilters